import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Coins, Gift, MapPin, Receipt, Save, ShoppingCart, Wallet } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/AppShell';
import { Page, Rise } from '@/components/motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Input } from '@/components/ui/input';
import { ErrorState, Skeleton } from '@/components/ui/misc';
import { adminApi } from '@/lib/api/endpoints';
import { isApiError, userMessage } from '@/lib/api/errors';
import type { CoinRules, CoinRulesIn } from '@/lib/api/types';
import { formatDateTime } from '@/lib/utils';

type FormState = Record<keyof CoinRulesIn, string> & { report_issue_place: string };

const toForm = (r: CoinRules): FormState => ({
  price_amount: r.price_amount,
  price_coins: String(r.price_coins),
  currency: r.currency,
  check_cost_coins: String(r.check_cost_coins),
  min_purchase_coins: String(r.min_purchase_coins),
  signup_bonus_coins: String(r.signup_bonus_coins),
  // Older API versions don't send it.
  report_issue_place: r.report_issue_place ?? '',
});

function PricingForm({ rules }: { rules: CoinRules }) {
  const qc = useQueryClient();
  const [f, setF] = React.useState<FormState>(() => toForm(rules));
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  React.useEffect(() => setF(toForm(rules)), [rules]);

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => setF((x) => ({ ...x, [k]: e.target.value }));
  const int = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s) : NaN);

  const save = useMutation({
    mutationFn: () =>
      adminApi.updateSettings({
        price_amount: f.price_amount.trim(),
        price_coins: int(f.price_coins),
        currency: f.currency.trim().toUpperCase(),
        check_cost_coins: int(f.check_cost_coins),
        min_purchase_coins: int(f.min_purchase_coins),
        signup_bonus_coins: int(f.signup_bonus_coins),
        // Omitted when empty, so the API keeps its current place.
        report_issue_place: f.report_issue_place.trim() || undefined,
      }),
    onSuccess: (r) => {
      qc.setQueryData(['admin', 'settings'], r);
      qc.invalidateQueries({ queryKey: ['pricing'] });
      toast.success('Pricing rules saved', { description: `${r.price_per_coin} ${r.currency} per coin.` });
      setErrors({});
    },
    onError: (e) => {
      if (isApiError(e, 422)) setErrors(e.fieldErrors);
      toast.error(userMessage(e));
    },
  });

  // Live preview of the per-coin price (display only; the API computes the real value).
  const amount = Number(f.price_amount);
  const coins = int(f.price_coins);
  const perCoin = amount > 0 && coins > 0 ? (amount / coins).toFixed(2) : '—';
  const dirty = JSON.stringify(f) !== JSON.stringify(toForm(rules));

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <Rise>
        <Card>
          <CardContent className="pt-6 sm:pt-6">
            <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
              <div>
                <p className="mb-3 flex items-center gap-2 text-sm font-semibold"><Wallet className="size-4 text-primary-ink" /> Price</p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Customer pays" htmlFor="price_amount" error={errors.price_amount}>
                    <Input id="price_amount" inputMode="decimal" value={f.price_amount} onChange={set('price_amount')} />
                  </Field>
                  <Field label="Currency" htmlFor="currency" error={errors.currency}>
                    <Input id="currency" className="uppercase" value={f.currency} onChange={set('currency')} />
                  </Field>
                  <Field label="…and gets coins" htmlFor="price_coins" error={errors.price_coins}>
                    <Input id="price_coins" inputMode="numeric" value={f.price_coins} onChange={set('price_coins')} />
                  </Field>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Check cost (coins)" htmlFor="check_cost_coins" error={errors.check_cost_coins} hint="0 makes checks free.">
                  <Input id="check_cost_coins" icon={<Receipt />} inputMode="numeric" value={f.check_cost_coins} onChange={set('check_cost_coins')} />
                </Field>
                <Field label="Minimum purchase" htmlFor="min_purchase_coins" error={errors.min_purchase_coins}>
                  <Input id="min_purchase_coins" icon={<ShoppingCart />} inputMode="numeric" value={f.min_purchase_coins} onChange={set('min_purchase_coins')} />
                </Field>
                <Field label="Sign-up bonus" htmlFor="signup_bonus_coins" error={errors.signup_bonus_coins} hint="0 turns it off.">
                  <Input id="signup_bonus_coins" icon={<Gift />} inputMode="numeric" value={f.signup_bonus_coins} onChange={set('signup_bonus_coins')} />
                </Field>
              </div>
              <div className="border-t pt-5">
                <p className="mb-3 flex items-center gap-2 text-sm font-semibold"><MapPin className="size-4 text-primary-ink" /> Status report</p>
                <Field label="Place of issue" htmlFor="report_issue_place" error={errors.report_issue_place} hint="Printed on every visa status report. Dubai, Abu Dhabi and Sharjah also get their Arabic name." className="sm:max-w-xs">
                  <Input id="report_issue_place" icon={<MapPin />} value={f.report_issue_place} maxLength={64} onChange={set('report_issue_place')} placeholder="Dubai" />
                </Field>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-5">
                <p className="text-xs text-muted-foreground">Last changed {formatDateTime(rules.updated_at)}. Pending purchases keep the price they were quoted.</p>
                <div className="flex gap-2">
                  {dirty && <Button type="button" variant="ghost" onClick={() => setF(toForm(rules))}>Reset</Button>}
                  <Button type="submit" variant="brand" loading={save.isPending} disabled={!dirty}>
                    <Save /> Save rules
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </Rise>
      <Rise>
        <Card className="relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-1 bg-brand-gradient animate-gradient-pan" />
          <CardHeader>
            <CardTitle>What customers see</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-xl bg-gradient-to-br from-brand-sun/10 to-brand-navy/10 p-4 text-center">
              <p className="text-xs text-muted-foreground">Per coin</p>
              <motion.p key={perCoin} initial={{ scale: 0.9, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }} className="text-3xl font-black">
                {perCoin} <span className="text-base font-semibold text-muted-foreground">{f.currency.toUpperCase()}</span>
              </motion.p>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2"><Coins className="size-4 text-amber-500" /> {f.price_amount || '—'} {f.currency.toUpperCase()} → {f.price_coins || '—'} coins</li>
              <li className="flex items-center gap-2"><Receipt className="size-4 text-primary-ink" /> {int(f.check_cost_coins) === 0 ? 'Checks are free' : `A check costs ${f.check_cost_coins} coin(s)`}</li>
              <li className="flex items-center gap-2"><ShoppingCart className="size-4 text-primary-ink" /> Buy at least {f.min_purchase_coins || '—'} coins</li>
              <li className="flex items-center gap-2"><MapPin className="size-4 text-primary-ink" /> Reports say issued in {f.report_issue_place.trim() || '—'}</li>
              <li className="flex items-center gap-2"><Gift className="size-4 text-emerald-500" /> {int(f.signup_bonus_coins) === 0 ? 'No sign-up bonus' : `${f.signup_bonus_coins} free coins on sign-up`}</li>
            </ul>
          </CardContent>
        </Card>
      </Rise>
    </div>
  );
}

export function AdminPricingPage() {
  const q = useQuery({ queryKey: ['admin', 'settings'], queryFn: adminApi.settings });
  return (
    <Page>
      <PageHeader eyebrow="Admin" title="Pricing rules" description="Changes apply right away and are recorded in the activity log." />
      {q.isPending ? <Skeleton className="h-80 w-full rounded-2xl" /> : q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : <PricingForm rules={q.data} />}
    </Page>
  );
}
